/**
 * Template Selector Component
 * Allows selecting a prompt template for an agent configuration
 */

import { useState } from 'react';
import { FileText, ChevronDown, Check, Eye } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { usePromptTemplates } from '@/features/prompt-templates';
import { useI18n } from '@/i18n';
import type { PromptTemplate } from '@/types/promptTemplates';

interface TemplateSelectorProps {
  value: number | null;
  onChange: (templateId: number | null) => void;
  onPreview?: (template: PromptTemplate) => void;
}

export function TemplateSelector({
  value,
  onChange,
  onPreview,
}: TemplateSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: templates = [], isLoading } = usePromptTemplates(true);
  const { messages } = useI18n();
  const t = messages.agentConfig.templateSelector;

  // Find selected template
  const selectedTemplate = templates.find(t => t.id === value);
  const defaultTemplate = templates.find(t => t.is_default);

  // Display text for current selection
  const getDisplayText = () => {
    if (value === null) {
      return defaultTemplate
        ? `${t?.useDefault || 'Use Default'} (${defaultTemplate.name})`
        : t?.useDefault || 'Use Default Template';
    }
    return selectedTemplate?.name || `Template #${value}`;
  };

  // Handle selection
  const handleSelect = (templateId: number | null) => {
    onChange(templateId);
    setIsOpen(false);
  };

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-5 h-5 text-primary-600" />
        <h3 className="font-semibold text-slate-900">{t?.title || 'Prompt Template'}</h3>
      </div>

      <p className="text-sm text-slate-600 mb-4">
        {t?.description || 'Select a template to define how the system prompt is structured for this agent.'}
      </p>

      {/* Dropdown Trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border border-slate-300 rounded-lg hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-slate-900">{getDisplayText()}</span>
            {value === null && defaultTemplate && (
              <span className="px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded">
                {t?.default || 'Default'}
              </span>
            )}
          </div>
          <ChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
            {/* Default Option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 border-b border-slate-100"
            >
              <div className="flex items-center gap-3">
                <span className="text-slate-900">
                  {defaultTemplate
                    ? `${t?.useDefault || 'Use Default'} (${defaultTemplate.name})`
                    : t?.useDefault || 'Use Default Template'}
                </span>
                <span className="px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded">
                  {t?.recommended || 'Recommended'}
                </span>
              </div>
              {value === null && <Check className="w-5 h-5 text-primary-600" />}
            </button>

            {/* Template Options */}
            {templates.map(template => (
              <button
                key={template.id}
                type="button"
                onClick={() => handleSelect(template.id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
              >
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-900">{template.name}</span>
                    {template.is_default && (
                      <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
                        {t?.systemDefault || 'System Default'}
                      </span>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-sm text-slate-500 mt-1 truncate">
                      {template.description}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    {(t?.sectionsCount || '{count} sections').replace('{count}', String(template.sections.length))} • {t?.updated || 'Updated'}{' '}
                    {new Date(template.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {value === template.id && (
                    <Check className="w-5 h-5 text-primary-600" />
                  )}
                </div>
              </button>
            ))}

            {templates.length === 0 && !isLoading && (
              <div className="px-4 py-6 text-center text-slate-500">
                {t?.noTemplates || 'No templates available'}
              </div>
            )}

            {isLoading && (
              <div className="px-4 py-6 text-center text-slate-500">
                {t?.loadingTemplates || 'Loading templates...'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Button */}
      {(value !== null ? selectedTemplate : defaultTemplate) && onPreview && (
        <Button
          variant="secondary"
          size="sm"
          className="mt-3"
          onClick={() => {
            const template = value !== null ? selectedTemplate : defaultTemplate;
            if (template) onPreview(template);
          }}
        >
          <Eye className="w-4 h-4 mr-2" />
          {t?.previewTemplate || 'Preview Template'}
        </Button>
      )}

      {/* Selected Template Info */}
      {(value !== null ? selectedTemplate : defaultTemplate) && (
        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
          <h4 className="text-sm font-medium text-slate-700 mb-2">
            {t?.sections || 'Template Sections'} ({(value !== null ? selectedTemplate : defaultTemplate)?.sections.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {(value !== null ? selectedTemplate : defaultTemplate)?.sections
              .filter(s => s.enabled)
              .sort((a, b) => a.order - b.order)
              .map(section => (
                <span
                  key={section.id}
                  className="px-2 py-1 text-xs bg-white border border-slate-200 rounded text-slate-600"
                >
                  {section.name}
                </span>
              ))}
          </div>
        </div>
      )}
    </Card>
  );
}
